import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import {
    BottomSheetService,
    FileService,
    IUploadBottomSheetOptions,
    NotificationMessageLevel,
    NotificationService,
    UploadBottomSheetResult,
    View,
} from '@lib/core';
import { DemandProduct } from '../../../types/DemandProduct';
import { Demand } from '../../../types/Demand';
import { DeliveryTimeUnit, DisplayCurrency, MobileService, SortingService } from '@lib/util';
import { Router } from '@angular/router';
import {
    CreateDemandProductService,
    IDemandAddProductDataForm,
    IDemandAddProductPriceForm,
} from '../../../create/create-demand-product.service';
import { HttpStatusCode } from '@angular/common/http';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDemandDialogComponent } from '../AbstractDemandDialogComponent';
import { firstValueFrom, Subject } from 'rxjs';
import { AccountAssignments, CustomAccount, ItemAccountingDataService } from '@lib/accounting';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { I18nService } from '@lib/i18n';
import { PurchaseRequisitionResult } from '../../../../purchase-requisition/types/PurchaseRequisitionResult';
import { RESULTS_IN } from '../edit-product/demand-edit-product.component';
import { ClientDataService } from '@lib/client';
import { EprocUser, UserService } from '@lib/user';
import { Dayjs } from 'dayjs';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import { DemandSelectableCurrencies } from '../../../types/IDemandSelectableCurrencies';

export class DemandAddProductDialogData {
    constructor(
        public demand: Demand,
        public readonly resultsIn: PurchaseRequisitionResult,
        public copiedProductId?: string,
    ) {}
}

export interface IDemandAddProductDeliveryForm {
    deliveryTime: FormControl<number>;
    deliveryTimeUnit: FormControl<DeliveryTimeUnit>;
    preferredDeliveryDate: FormControl<Dayjs>;
    partialDeliveryPossible: FormControl<boolean>;
}

@Component({
    selector: 'create-add-product',
    templateUrl: './demand-add-product.component.html',
    styleUrls: ['./demand-add-product.component.scss'],
    standalone: false,
})
export class DemandAddProductComponent
    extends AbstractDemandDialogComponent<DemandAddProductDialogData>
    implements OnInit, OnDestroy
{
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly mobileService = inject(MobileService);
    private readonly router = inject(Router);
    private readonly bottomSheetService = inject(BottomSheetService);
    private readonly fileService = inject(FileService);
    private readonly itemAccountingDataService = inject(ItemAccountingDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly i18n = inject(I18nService);
    private readonly userService = inject(UserService);
    private readonly clientDataService = inject(ClientDataService);
    private readonly breadcrumbService = inject(BreadcrumbService);

    public demand!: Demand;
    public product!: DemandProduct;
    public defaultArticleNumber!: string;
    public deliveryDateRequired!: boolean;
    public supplierArticleNumberRequired!: boolean;

    public dataGroup?: FormGroup<IDemandAddProductDataForm>;
    public deliveryGroup?: FormGroup<IDemandAddProductDeliveryForm>;
    public priceGroup?: FormGroup<IDemandAddProductPriceForm>;

    public commentCtrl = new FormControl<string>('', { nonNullable: true });
    public internalCommentCtrl = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.maxLength(250)],
    });

    public View = View;

    public uploadData: UploadBottomSheetResult[] = [];

    public error?: HttpStatusCode;
    public user!: EprocUser;
    public activeCurrencies?: DisplayCurrency[];
    /** ISO codes the customer does not allow (EPROC-11425), hidden from the dropdown. */
    public customerExcludedCurrencies = new Set<string>();
    /** ISO codes the customer allows but a supplier of the demand does not accept (EPROC-11425). */
    public supplierExcludedCurrencies = new Set<string>();

    public onDestroy$: Subject<void> = new Subject<void>();

    public readonly saving = signal(false);
    /** Position being duplicated; only the first position saved from this form becomes its copy. */
    private copySourceProductId?: string;
    /**
     * Fixed on init: navigating back closes the dialog while a save is still running, and what follows that
     * save must neither turn into the page flow nor navigate once the user has left.
     */
    private openedAsDialog = false;
    private destroyed = false;

    public get heading(): string {
        return this.demand.isAnnounced
            ? marker('Artikel der Bedarfsmeldung hinzufügen')
            : marker('Artikel dem Bedarf hinzufügen');
    }

    public get backBtnText(): string {
        return this.demand.isAnnounced ? marker('zurück zur Bedarfsmeldung') : marker('zurück zum Bedarf');
    }

    private get resultsIn(): PurchaseRequisitionResult {
        if (this.insideDialog && this.dialogData?.resultsIn) {
            return this.dialogData.resultsIn;
        }
        if (this.route.snapshot.queryParamMap.has(RESULTS_IN)) {
            return this.route.snapshot.queryParamMap.get(RESULTS_IN) as PurchaseRequisitionResult;
        }

        return PurchaseRequisitionResult.RFQ;
    }

    constructor() {
        super();
    }

    public getDemand(): Promise<Demand> {
        if (this.insideDialog && this.dialogData) {
            return Promise.resolve(this.dialogData.demand);
        }

        const demandId: string = this.route.snapshot.params['demandId'];
        this.breadcrumbService.set(
            BC.PURCHASE_REQUISITION.LIST,
            BC.PURCHASE_REQUISITION.DEMAND_CREATE,
            BC.PURCHASE_REQUISITION.DEMAND_ADD_ITEM(demandId, this.resultsIn),
        );
        return Demand.byId(
            demandId,
            this.dataService,
            this.commentDataService,
            this.itemClassificationDataService,
            this.fileDataService,
            this.i18n,
        );
    }

    async ngOnInit(): Promise<void> {
        this.openedAsDialog = this.insideDialog;
        try {
            this.demand = await this.getDemand();
            await this.getCurrencies();
            const settings = await firstValueFrom(this.clientDataService.getSettings());
            this.defaultArticleNumber = settings.rfq.defaultArticleNumber;
            this.deliveryDateRequired = settings.deliveryDateRequired;
            this.supplierArticleNumberRequired = settings.supplierArticleNumberRequired;
            this.user = await this.userService.getMyself();
            this.copySourceProductId = this.readCopySourceProductId();
            this.initProduct();
            if (!this.demand.readOnly) {
                this.initForms();
            } else {
                this.error = HttpStatusCode.Forbidden;
            }
        } catch {
            this.error = HttpStatusCode.NotFound;
        }
    }

    public ngOnDestroy() {
        this.destroyed = true;
        this.onDestroy$.next();
        this.onDestroy$.complete();
        this.closeDialog(this.demand);
    }

    public upload(): void {
        // Attachments are taken over at the start of a save; one added meanwhile would get lost.
        if (this.saving()) {
            return;
        }
        const options: IUploadBottomSheetOptions = {
            uploadInternalPossible: true,
            multiple: false,
        };
        this.bottomSheetService
            .upload(options)
            ?.afterDismissed()
            .subscribe(async (result?: UploadBottomSheetResult) => {
                if (result) {
                    this.uploadData.push(result);
                }
            });
    }

    public download(entry: UploadBottomSheetResult): void {
        if (!entry) {
            return;
        }
        let file: File | undefined;
        entry.formData.forEach((value) => {
            if (!file && value instanceof File) {
                file = value;
            }
        });
        if (file) {
            this.fileService.saveFile(file.name, file, file.type);
        }
    }

    public removeFile(entryToRemove: UploadBottomSheetResult): void {
        if (entryToRemove && !this.saving()) {
            this.uploadData = this.uploadData.filter((entry) => entry !== entryToRemove);
        }
    }

    public getCurrentLanguage(): string {
        return this.i18n.currentLanguage;
    }

    /** Returns whether the position was saved; a second click while saving is ignored. */
    public async saveNewPosition(shouldRedirectAfterSave = true): Promise<boolean> {
        if (this.saving() || !(this.dataGroup?.valid && this.priceGroup?.valid && this.deliveryGroup?.valid)) {
            return false;
        }
        this.setSaving(true);
        try {
            const service: CreateDemandProductService = new CreateDemandProductService();
            service.insertDataIntoProduct(
                this.product,
                this.dataGroup,
                this.priceGroup,
                this.deliveryGroup,
                this.commentCtrl.value,
                this.internalCommentCtrl.value,
            );
            let newProduct: DemandProduct | null;
            try {
                newProduct = await this.demand.addProduct(this.product, (created) => this.completeNewProduct(created));
            } catch {
                // Creating the position failed, so nothing was saved; reported by the HTTP error handler.
                return false;
            }
            if (!newProduct) {
                return false;
            }
            if (shouldRedirectAfterSave && !this.destroyed) {
                if (this.openedAsDialog) {
                    this.closeDialog(this.demand);
                } else {
                    await this.router.navigate([View.CLIENT, 'rfq', 'demand', this.demand.id]);
                }
            }
            return true;
        } finally {
            this.setSaving(false);
        }
    }

    /** Closing is held back while saving, so the list behind the dialog only updates once the position is complete. */
    public closeDialogUnlessSaving(): void {
        if (!this.saving()) {
            this.closeDialog(this.demand);
        }
    }

    private setSaving(saving: boolean): void {
        this.saving.set(saving);
        if (this.dialogRef) {
            // Escape and backdrop clicks must not close the dialog halfway through either.
            this.dialogRef.disableClose = saving;
        }
    }

    /**
     * Writes everything that is not part of the position itself, before the demand notifies its listeners
     * (see Demand.addProduct). Must not reject, so every step reports its own failure.
     */
    private async completeNewProduct(newProduct: DemandProduct): Promise<void> {
        if (this.uploadData.length) {
            try {
                for (const entry of this.uploadData) {
                    await newProduct.addFile(entry.formData, entry.internal);
                }
            } catch {
                const message = marker('Beim Hochladen der Anhänge ist leider ein Fehler aufgetreten.');
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            }
        }
        const sourceProductId = this.copySourceProductId;
        if (sourceProductId) {
            try {
                await this.copyAccountAssignments(sourceProductId, newProduct);
                await this.copyClassifications(sourceProductId, newProduct);
                await this.copySiteCode(sourceProductId, newProduct);
            } catch {
                const message = marker(
                    'Die Position wurde angelegt, Kontierung, Klassifikation oder Standort der kopierten Position konnten aber nicht übernommen werden.',
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            }
        }
    }

    public async createAndSaveNewPosition(): Promise<void> {
        if (!(await this.saveNewPosition(false))) {
            return;
        }
        this.endCopy();

        this.product = new DemandProduct(
            this.dataService,
            this.itemClassificationDataService,
            this.fileDataService,
            this.demand.id,
        );

        this.initForms();

        this.commentCtrl.setValue('');
        this.internalCommentCtrl.setValue('');
        this.uploadData = [];
        const message = marker('Position wurde zur Bedarfsmeldung hinzugefügt');
        this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
    }

    /** Only the position saved from the duplicated one is a copy; the following ones start empty. */
    private endCopy(): void {
        if (!this.copySourceProductId) {
            return;
        }
        this.copySourceProductId = undefined;
        if (!this.openedAsDialog && !this.destroyed) {
            // Reloading the page must not open the copy form again.
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { copy: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }
    }

    private readCopySourceProductId(): string | undefined {
        const sourceProductId =
            this.insideDialog && this.dialogData
                ? this.dialogData.copiedProductId
                : this.route.snapshot.queryParams['copy'];
        // A stale bookmark or a source removed elsewhere starts an ordinary, empty position.
        return sourceProductId && this.demand.hasProduct(sourceProductId) ? sourceProductId : undefined;
    }

    private initForms(): void {
        const service: CreateDemandProductService = new CreateDemandProductService();
        this.dataGroup = service.initDataFormGroup(
            this.formBuilder,
            this.product,
            this.demand,
            this.supplierArticleNumberRequired,
            this.defaultArticleNumber,
        );
        this.priceGroup = service.initPriceFormGroup(this.formBuilder, this.product, this.user);
        this.deliveryGroup = service.initDeliveryFormGroup(this.formBuilder, this.product, this.deliveryDateRequired);
    }

    // EPROC-11619: this used to rebuild the whole article list (getProductsAsArticles(), re-fetching
    // accounting/classification for every position) just to look up the one copied position - and did
    // so twice, once here and once in copyClassifications(). Fetch just that one position's accounting
    // assignments directly instead.
    private async copyAccountAssignments(sourceProductId: string, newProduct: DemandProduct): Promise<void> {
        const source = await firstValueFrom(
            this.itemAccountingDataService.retrieveAccountAssignments(this.demand.id, sourceProductId),
        );
        const accountAssignments: AccountAssignments = AccountAssignments.fromArticleAccountAssignments(
            source.costCenterAccountKey?.value ?? '',
            source.generalLedgerAccountKey?.value ?? '',
            source.projectAccountKey?.value ?? '',
            source.customAccounts.map((customAccount) =>
                CustomAccount.fromInterface(customAccount).toCustomAccountAssignment(),
            ),
        );
        await firstValueFrom(
            this.itemAccountingDataService.updateAllAccountAssignments(
                this.demand.id,
                newProduct.itemId!,
                accountAssignments,
            ),
        );
    }

    private async copyClassifications(sourceProductId: string, newProduct: DemandProduct): Promise<void> {
        const classification = await firstValueFrom(
            this.itemClassificationDataService.getClassification(this.demand.id, sourceProductId),
        );
        if (classification) {
            await firstValueFrom(
                this.itemClassificationDataService.changeClassification(
                    this.demand.id,
                    newProduct.itemId!,
                    classification,
                ),
            );
        }
    }

    // EPROC-11007: Standort (WERKS) der Quellposition auf die kopierte Position übernehmen.
    // siteCode ist nicht Teil von addProduct (toDto), sondern wird – analog zu Kontierungen
    // und Klassifikationen – über den dedizierten Endpoint nachgezogen.
    private async copySiteCode(sourceProductId: string, newProduct: DemandProduct): Promise<void> {
        const sourceProduct = this.demand.getProduct(sourceProductId);
        if (sourceProduct?.siteCode) {
            // The server re-derives the shipping address from the position sites; take over what it reports.
            const change = await firstValueFrom(
                this.dataService.updateItemSite(this.demand.id, newProduct.itemId!, sourceProduct.siteCode),
            );
            newProduct.siteCode = sourceProduct.siteCode;
            this.demand.takeOverShippingAddressChange(change);
        }
    }

    private initProduct() {
        const sourceProduct = this.copySourceProductId ? this.demand.getProduct(this.copySourceProductId) : undefined;
        if (sourceProduct) {
            this.product = DemandProduct.copy(
                sourceProduct,
                this.demand.id,
                this.dataService,
                this.itemClassificationDataService,
                this.fileDataService,
            );
        } else {
            this.product = new DemandProduct(
                this.dataService,
                this.itemClassificationDataService,
                this.fileDataService,
                this.demand.id,
            );
        }
    }

    /**
     * Loads the currency dropdown and the exclusions behind its hints
     * ({@link DemandSelectableCurrencies.exclusionsAmong}, EPROC-11425). Requires the demand to be
     * loaded: the selectable currencies depend on its suppliers.
     */
    private async getCurrencies(): Promise<void> {
        const user: EprocUser = await this.userService.getMyself();
        const [activeIsos, selectable] = await Promise.all([
            firstValueFrom(this.clientDataService.getActiveCurrencies()),
            firstValueFrom(this.dataService.getSelectableCurrencies(this.demand.id)).catch(
                (): DemandSelectableCurrencies | undefined => undefined,
            ),
        ]);
        this.activeCurrencies = activeIsos.map((iso) => new DisplayCurrency(iso, user.language.iso639LanguageAlpha2));
        SortingService.sortSortables(this.activeCurrencies);
        const exclusions = DemandSelectableCurrencies.exclusionsAmong(activeIsos, selectable);
        this.customerExcludedCurrencies = exclusions.byCustomer;
        this.supplierExcludedCurrencies = exclusions.bySupplier;
    }

    private get dialogData(): DemandAddProductDialogData | undefined {
        if (this.insideDialog) {
            return this.injector.get<DemandAddProductDialogData>(MAT_DIALOG_DATA);
        }

        return undefined;
    }
}
